import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { QueueService } from '@/services/queue-service';
import { formatNumberByKilo } from '@/utils/format-number';
import { AISearchRepository } from './ai-search.repository.interface';
import { TechStrategyModel } from '@/services/ai-search/model/tech-strategy.model';
import type { TechStrategyState, QueryListItem } from '@/services/ai-search/type/query-transform.type';
import type { ElementsState } from '@/services/ai-search/type/ai-search.type';
import { QueryTransformService } from '@/services/ai-search/service/query-transform.service';
import { RavenInsertMeta } from 'library-@/services/sentry';
import { OpenService } from '@analytics/shared/services/open-service';

export const AISearchStrategyService = Symbol('AISearchStrategyService');

export interface AISearchStrategyService {
    readonly onStatesChange: Event<TechStrategyState>;
    getModelData: () => TechStrategyState;
    goSearchResult: (str: string) => void;
    handleSaveToProjectSearch: () => void;
    init(func: () => Promise<ElementsState>): Promise<void>;
    dispose: () => void;
}

@injectable()
export class AISearchStrategyServiceImpl implements AISearchStrategyService {
    private _onStatesChangeEmitter = new Emitter<TechStrategyState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AISearchRepository)
        private _repository: AISearchRepository,
        @inject(TechStrategyModel)
        private _model: TechStrategyModel,
        @inject(QueueService)
        private _queueService: QueueService,
        @inject(OpenService)
        private _openService: OpenService,
        @inject(QueryTransformService)
        private _queryTransformService: QueryTransformService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): TechStrategyState {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<TechStrategyState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    private _getQueryCount(queryList: QueryListItem[]) {
        queryList.forEach(async (item, index) => {
            const q = item.string;
            const task = {
                key: `${ q }-${ index }`,
                fn: () => this._repository.getQueryCount(q),
            };

            const res = await this._queueService.push(task);

            queryList[index].count = formatNumberByKilo(res);
            queryList[index].loading = false;
            
            if (res) {
                this._setModelData({ queryList });
            } else {
                this._setModelData({ queryList: queryList.filter(item => item.count !== '0') });
            }
        });
    }


    private _getSearchResultUrl(query: { [key: string]: string }): string {
        const params = new URLSearchParams();
        Object.keys(query).forEach(key => {
            const value = query[key];
            if (value !== undefined) {
                params.append(key, query[key]);
            }
        });
        return `/search/result?${ params.toString() }`;
    }

    async goSearchResult(query: string) {
        const data = await this._repository.transferQuery({
            query,
            search_from: 'project_search',
        });
        const url = this._getSearchResultUrl({
            ...data,
            search_mode: 'publication',
        });

        this._openService.open(url, {});
    }

    async handleSaveToProjectSearch() {
        const { saveLoading, queryList } = this.getModelData();

        if (saveLoading) return;
        this._setModelData({ saveLoading: true });

        const projectQueryList = queryList.map(item => {
            return { queryBody: item.codeQuery || item.string, comment: item.desc };
        });
        const projectId = await this._repository.setTechQuery(projectQueryList);

        this._setModelData({ saveLoading: false });

        if (projectId) {
            RavenInsertMeta('AI Search', 'Save AI Search Strategy');
            this._openService.open(
                `/search/input/field/project?project_item_key=${ projectId }`,
                {},
            );
        }
    }

    async init(func: () => Promise<ElementsState>) {
        this._queueService.initQueue({ concurrency: 1 });

        const pendingList = this._queryTransformService.transformQuery(await func());
        const queryList = pendingList.map(item => {
            return {
                ...item,
                loading: true,
                count: '',
            };
        });

        this._setModelData({ queryList, loading: false });
        this._getQueryCount(queryList);
    }

    dispose() {
        this._queueService.dispose();
    }
}
